<script setup lang="ts">
import { ArrowLeft, ChevronLeft, LayoutGrid, Share2, ChevronRight } from 'lucide-vue-next';
import demoShareImg from '@/assets/images/demo-share.png';

const props = defineProps<{
  showSharePreview: boolean;
  currentIndex?: number;
  currentImage?: string;
}>();
</script>

<template>
  <div class="share-stage-full">
    <!-- Header Navigation -->
    <header class="share-nav">
      <div class="s-nav-back-wrap">
        <div class="s-back-circle"><ArrowLeft :size="16" /></div>
        <span class="s-back-text">Back</span>
      </div>
    </header>

    <!-- Tab Toggle -->
    <div class="preview-toggle-container">
      <div class="toggle-pill active">Preview</div>
      <div class="toggle-pill">Templates</div>
    </div>

    <!-- Retro Image Preview -->
    <div class="preview-canvas-container">
      <div class="retro-canvas-image-wrap">
        <img 
          :src="currentImage || demoShareImg" 
          alt="Share Preview" 
          class="retro-demo-img" 
        />
      </div>
    </div>

    <!-- Custom Footer Navigation -->
    <footer class="preview-footer-modern">
      <div class="f-nav-group">
        <button class="f-nav-btn circle dark"><ChevronLeft :size="20" /></button>
        <span class="f-page-indicator">{{ currentIndex || 1 }} of 36</span>
        <button class="f-nav-btn circle red"><ChevronRight :size="20" /></button>
      </div>
      <div class="f-action-group">
        <button class="f-nav-btn square red"><LayoutGrid :size="20" /></button>
        <button class="f-share-btn-pill">
          <Share2 :size="18" />
          <span>Share</span>
        </button>
      </div>
    </footer>
  </div>
</template>

<style scoped>
.share-stage-full { position: absolute; inset: 0; background: #111113; z-index: 200; display: flex; flex-direction: column; }

/* Header & Tabs */
.share-nav { padding: 34px 12px 4px; }
.s-nav-back-wrap { display: flex; align-items: center; gap: 8px; cursor: pointer; }
.s-back-circle { width: 28px; height: 28px; background: rgba(255,255,255,0.1); border-radius: 50%; display: flex; align-items: center; justify-content: center; color: #fff; }
.s-back-text { color: #fff; font-weight: 700; font-size: 13px; }

.preview-toggle-container { background: #1c1c1e; border-radius: 8px; margin: 4px 12px 10px; display: flex; padding: 3px; }
.toggle-pill { flex: 1; text-align: center; padding: 6px; border-radius: 6px; font-size: 11px; font-weight: 700; color: #8e8e93; transition: all 0.3s ease; }
.toggle-pill.active { background: #e21b0d; color: #fff; box-shadow: 0 2px 10px rgba(226, 27, 13, 0.4); }

.preview-canvas-container { 
  flex: 1; 
  padding: 10px 30px; 
  display: flex; 
  align-items: center; 
  justify-content: center; 
  overflow: hidden;
}
.retro-canvas-image-wrap { 
  width: 100%; 
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 20px 50px rgba(0,0,0,0.5);
  border-radius: 4px;
  overflow: hidden;
}
.retro-demo-img {
  width: 100%;
  height: 100%;
  object-fit: contain;
  background: #000;
}

/* Modern Footer */
.preview-footer-modern { 
  padding: 12px 16px 36px; 
  display: flex; 
  justify-content: space-between; 
  align-items: center; 
  background: #000;
  border-top: 1px solid rgba(255,255,255,0.05);
}
.f-nav-group { display: flex; align-items: center; gap: 6px; }
.f-nav-btn { display: flex; align-items: center; justify-content: center; cursor: pointer; transition: all 0.2s; border: none; }
.f-nav-btn:active { transform: scale(0.9); }
.f-nav-btn.circle { width: 34px; height: 34px; border-radius: 10px; }
.f-nav-btn.square { width: 34px; height: 34px; border-radius: 10px; }
.f-nav-btn.red { background: #e21b0d; color: #fff; box-shadow: 0 4px 10px rgba(226, 27, 13, 0.3); }

.f-page-indicator { color: #fff; font-weight: 600; font-size: 11px; min-width: 44px; text-align: center; }
.f-action-group { display: flex; align-items: center; gap: 12px; }
.f-share-btn-pill { 
  background: #e21b0d; 
  border: none; 
  padding: 0 14px; 
  height: 34px; 
  border-radius: 100px; 
  color: #fff; 
  display: flex; 
  align-items: center; 
  gap: 6px; 
  font-weight: 700; 
  font-size: 12px; 
  cursor: pointer; 
  box-shadow: 0 4px 12px rgba(226, 27, 13, 0.3);
  white-space: nowrap;
}
</style>
